import { Loader2, MapPin } from 'lucide-react';
import { WorkshopV2 } from '../../../interfaces/workshop.interface';
import { Participant } from '../../../interfaces/reservation.interface';
import { ParticipantsState } from '../../history/components/HistoryStats';
import {
  fillRate,
  headcountOf,
  longDate,
  placesOf,
} from '../utils/dashboardData';

/**
 * La feuille d'émargement du matin : qui franchit la porte, à quelle heure, où,
 * et comment le joindre. C'est le seul élément vraiment neuf de la page — sans
 * lui il faut ouvrir /workshops puis un popup atelier par atelier.
 */

interface Props {
  workshops: WorkshopV2[];
  participants: Record<string, Participant[]>;
  participantsState: ParticipantsState;
  participantsError: string | null;
  /** Affiché quand la journée est vide, pour ne pas laisser le panneau muet. */
  nextWorkshop: WorkshopV2 | null;
  loading: boolean;
}

/**
 * Badge à trois valeurs. Le raccourci `type === 'duo' ? 'Duo' : 'Solo'` employé
 * ailleurs dans l'interface compte les réservations héritées (`type === null`)
 * en Solo : ce serait une donnée inventée, ici on l'affiche pour ce qu'elle est.
 */
const FormulaBadge = ({ type }: { type: Participant['type'] }) => {
  const style =
    type === 'duo'
      ? 'bg-purple-100 text-purple-700'
      : type === 'solo'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-gray-100 text-gray-600';

  const label =
    type === 'duo' ? 'Duo' : type === 'solo' ? 'Solo' : 'Non renseigné';

  return (
    <span className={`rounded-full px-2 py-1 text-xs font-medium ${style}`}>
      {label}
    </span>
  );
};

const Skeleton = () => (
  <div className="px-6 py-4">
    {[...Array(2)].map((_, i) => (
      <div key={i} className="mt-4 first:mt-0">
        <div className="h-4 w-48 rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
        <div className="mt-2 h-3 w-64 rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
        <div className="mt-3 h-24 w-full rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
      </div>
    ))}
  </div>
);

const TodayPanel = ({
  workshops,
  participants,
  participantsState,
  participantsError,
  nextWorkshop,
  loading,
}: Props) => {
  const ready = participantsState === 'ready';
  const headcount = ready
    ? headcountOf(workshops.flatMap((w) => participants[w.id] || []))
    : 0;

  return (
    <section className="rounded-2xl border border-[#623413]/10 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
        <h3 className="font-semibold text-[#623413]">Aujourd'hui</h3>
        <span className="text-sm text-gray-500">
          {workshops.length === 0
            ? '—'
            : ready
              ? `${headcount} personne${headcount > 1 ? 's' : ''} attendue${
                  headcount > 1 ? 's' : ''
                }`
              : '—'}
        </span>
      </div>

      {loading ? (
        <Skeleton />
      ) : workshops.length === 0 ? (
        <>
          <p className="py-10 text-center text-sm text-gray-500">
            Aucun atelier aujourd'hui.
          </p>
          {nextWorkshop && (
            <p className="pb-6 text-center text-xs text-gray-400 first-letter:uppercase">
              Prochain : {nextWorkshop.title},{' '}
              {longDate(nextWorkshop.date.toDate())}.
            </p>
          )}
        </>
      ) : (
        <div className="px-6 py-4">
          {workshops.map((workshop) => {
            const rows = participants[workshop.id] || [];
            const places = placesOf(rows);
            const capacity = workshop.maxParticipants || 0;

            return (
              <div
                key={workshop.id}
                className="mt-4 border-t border-gray-100 pt-4 first:mt-0 first:border-0 first:pt-0"
              >
                <p className="font-medium text-[#623413]">{workshop.title}</p>
                <p className="text-sm text-gray-600 first-letter:uppercase">
                  {longDate(workshop.date.toDate())}
                </p>
                {workshop.location && (
                  <p className="flex items-center gap-1 text-sm text-gray-500">
                    <MapPin size={13} className="shrink-0" />
                    <span className="truncate">{workshop.location}</span>
                  </p>
                )}

                {/* Jauge masquée tant que les réservations manquent : une barre
                    à 0 % se lirait comme « personne ne vient ». */}
                {ready && (
                  <>
                    <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-neutral-100">
                      <div
                        className="h-full rounded-full bg-[#623413]"
                        style={{ width: `${fillRate(places, capacity)}%` }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      {places} / {capacity} places
                    </p>
                  </>
                )}

                {participantsState === 'loading' ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-gray-500">
                    <Loader2 size={18} className="animate-spin" />
                    Chargement…
                  </div>
                ) : participantsState === 'unavailable' ? (
                  <p className="py-6 text-center text-sm text-[#BB0F33]">
                    {participantsError}
                  </p>
                ) : rows.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">
                    Aucune réservation pour cet atelier.
                  </p>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                          <th className="px-6 py-3 font-medium">Nom</th>
                          <th className="px-4 py-3 font-medium">Téléphone</th>
                          <th className="px-6 py-3 font-medium">Formule</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr
                            key={row.id}
                            className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60"
                          >
                            <td className="px-6 py-3 text-gray-800">
                              {row.displayName}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {row.phone || '—'}
                            </td>
                            <td className="px-6 py-3">
                              <FormulaBadge type={row.type} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default TodayPanel;
